import { useI18n } from '@/hooks/useI18n';
import { useMemo, useState } from 'react';
import { Plus, Tags } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PathLabel } from '@/components/primitives/PathLabel';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { Empty } from '@/components/primitives/Empty';
import { Stagger } from '@/components/primitives/Stagger';
import { useIdentities } from '@/hooks/queries/useIdentities';
import { useIdentityPerformance } from '@/hooks/queries/useIdentityPerformance';
import type { IdentityResponse, IdentityPerformance } from '@/types/api';
import { IdentityRow } from './IdentityRow';
import { IdentityUpsertDialog } from './IdentityUpsertDialog';

export function IdentitiesScreen() {
  const { t } = useI18n();
  const [editing, setEditing] = useState<IdentityResponse | null>(null);
  const [creating, setCreating] = useState(false);

  const identities = useIdentities();
  const perf = useIdentityPerformance(30);

  const perfMap = useMemo(() => {
    const map = new Map<string, IdentityPerformance>();
    (perf.data ?? []).forEach((p) => map.set(p.identityId, p));
    return map;
  }, [perf.data]);

  const list = identities.data ?? [];

  return (
    <Stagger className="space-y-5">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <PathLabel
            path="/identities"
            meta={t(`${list.length} · perf 30j`, `${list.length} · 30d performance`)}
          />
          <h1 className="mt-1 flex items-center gap-2 text-[22px] font-semibold tracking-tight text-text-1">
            <Tags className="h-5 w-5 text-violet-deep" />
            {t('Identités', 'Identities')}
          </h1>
          <p className="text-[13px] text-text-3">
            {t(
              'Regroupez vos comptes par thème pour suivre leur performance.',
              'Group your accounts by theme to track their performance.',
            )}
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          className="w-full gap-1.5 sm:w-auto sm:shrink-0"
          onClick={() => setCreating(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          {t('Nouvelle identité', 'New identity')}
        </Button>
      </header>

      {identities.error ? (
        <ErrorInline error={identities.error} onRetry={() => identities.refetch()} />
      ) : identities.isLoading ? (
        <div className="space-y-px overflow-hidden rounded-[14px] border border-line-1 bg-bg-1 shadow-card">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full rounded-none lg:h-16" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <Empty
          title={t('Aucune identité', 'No identities')}
          description={t(
            'Créez votre première identité pour regrouper vos comptes.',
            'Create your first identity to group your accounts.',
          )}
          action={
            <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
              <Plus className="h-3 w-3" />
              {t('Créer une identité', 'Create an identity')}
            </Button>
          }
        />
      ) : (
        <section className="overflow-hidden rounded-[14px] border border-line-1 bg-bg-1 shadow-card">
          <div className="divide-y divide-line-1/70">
            {list.map((i) => (
              <IdentityRow
                key={i.id}
                identity={i}
                perf={perfMap.get(i.id) ?? null}
                onEdit={() => setEditing(i)}
              />
            ))}
          </div>
        </section>
      )}

      <IdentityUpsertDialog
        identity={editing}
        open={!!editing || creating}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setCreating(false);
          }
        }}
      />
    </Stagger>
  );
}
